import {
  Box,
  Button,
  Dialog,
  Heading,
  HStack,
  Input,
  Portal,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react"
import { useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useOrganisationMembers, useUserDetails } from "../hooks/useAuthQueries"
import { useDeletePortfolio, usePortfolios, useRenamePortfolio } from "../hooks/usePortfolioQueries"

const ORGANISATION_TYPE_LABELS: Record<string, string> = {
  property_investment: "Property investment / Asset management",
  housing_association: "Housing association",
  local_authority: "Local authority",
  other: "Other"
}

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <HStack
    py={3}
    borderBottom="1px solid"
    borderColor="gray.100"
    justify="space-between"
    align="center"
    _last={{ borderBottom: "none" }}
  >
    <Text fontSize="sm" color="gray.500" minW="180px">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="medium" textAlign="right">
      {value}
    </Text>
  </HStack>
)

const MemberAvatar: React.FC<{ firstName: string; lastName: string }> = ({ firstName, lastName }) => {
  const initials = `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase()
  return (
    <Box
      w="36px"
      h="36px"
      borderRadius="full"
      bg="green.100"
      color="green.800"
      display="flex"
      alignItems="center"
      justifyContent="center"
      fontSize="xs"
      fontWeight="bold"
      flexShrink={0}
    >
      {initials}
    </Box>
  )
}

const Settings: React.FC = () => {
  const navigate = useNavigate()
  const { data: userDetails, isLoading: isLoadingUser } = useUserDetails()
  const { data: members, isLoading: isLoadingMembers } = useOrganisationMembers()
  const { data: portfolios = [], isLoading: isLoadingPortfolios } = usePortfolios()
  const deletePortfolioMutation = useDeletePortfolio()
  const renamePortfolioMutation = useRenamePortfolio()
  const organisation = userDetails?.data?.organisation
  const organisationOwnerId = organisation?.ownerId
  const activePortfolioId = userDetails?.data?.activePortfolioId

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)
  const portfolioToDelete = portfolios.find((p) => p.id === confirmDeleteId)

  const [renamingId, setRenamingId] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState("")
  const renameInputRef = useRef<HTMLInputElement>(null)

  const startRename = (id: number, currentName: string) => {
    setRenamingId(id)
    setRenameValue(currentName)
    setTimeout(() => renameInputRef.current?.focus(), 50)
  }

  const cancelRename = () => {
    setRenamingId(null)
    setRenameValue("")
  }

  const handleRename = async () => {
    const name = renameValue.trim()
    if (!name || renamingId == null) return
    await renamePortfolioMutation.mutateAsync({ id: renamingId, name })
    setRenamingId(null)
    setRenameValue("")
  }

  const handleDelete = async () => {
    if (confirmDeleteId == null) return
    await deletePortfolioMutation.mutateAsync(confirmDeleteId)
    setConfirmDeleteId(null)
  }

  const quotaUsed = organisation?.totalHomes ?? 0
  const quotaTotal = organisation?.homeQuota ?? 10
  const quotaPct = Math.min((quotaUsed / quotaTotal) * 100, 100)
  const quotaColor =
    quotaPct >= 100 ? "red.400" : quotaPct >= 80 ? "orange.400" : "green.400"

  return (
    <Box p={8} maxW="780px">
      <VStack gap={8} align="stretch">

        {/* Page header */}
        <Box>
          <Heading size="xl" mb={1}>Settings</Heading>
          <Text color="gray.500" fontSize="sm">
            Manage your organisation, portfolios and team.
          </Text>
        </Box>

        {/* Organisation */}
        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200">
          <Box px={6} pt={5} pb={4} borderBottom="1px solid" borderColor="gray.100">
            <Text fontWeight="semibold" fontSize="md">Organisation</Text>
          </Box>
          <Box px={6} py={2}>
            {isLoadingUser ? (
              <Box py={6}><Spinner size="sm" /></Box>
            ) : organisation ? (
              <>
                <InfoRow label="Name" value={organisation.name} />
                {organisation.companyNumber && (
                  <InfoRow label="Company number" value={organisation.companyNumber} />
                )}
                {organisation.organisationType && (
                  <InfoRow
                    label="Type"
                    value={ORGANISATION_TYPE_LABELS[organisation.organisationType] ?? organisation.organisationType}
                  />
                )}
              </>
            ) : (
              <Text py={4} color="gray.500" fontSize="sm">No information available.</Text>
            )}
          </Box>
        </Box>

        {/* Plan & quota */}
        {organisation && (
          <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200">
            <Box px={6} pt={5} pb={4} borderBottom="1px solid" borderColor="gray.100">
              <Text fontWeight="semibold" fontSize="md">Plan</Text>
            </Box>
            <Box px={6} py={5}>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color="gray.600">Homes used</Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {quotaUsed} / {quotaTotal}
                </Text>
              </HStack>
              <Box h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                <Box
                  h="100%"
                  w={`${quotaPct}%`}
                  bg={quotaColor}
                  borderRadius="full"
                  transition="width 0.3s ease"
                />
              </Box>
              <Text fontSize="xs" color="gray.400" mt={2}>
                {quotaTotal - quotaUsed} slot{quotaTotal - quotaUsed !== 1 ? "s" : ""} remaining on your free plan
              </Text>
            </Box>
          </Box>
        )}

        {/* Portfolios */}
        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200">
          <HStack px={6} pt={5} pb={4} borderBottom="1px solid" borderColor="gray.100" justify="space-between">
            <Text fontWeight="semibold" fontSize="md">Portfolios</Text>
            <Button
              size="sm"
              variant="outline"
              colorPalette="green"
              onClick={() => navigate("/create-portfolio")}
            >
              + New portfolio
            </Button>
          </HStack>
          <Box px={6} py={2}>
            {isLoadingPortfolios ? (
              <Box py={6}><Spinner size="sm" /></Box>
            ) : portfolios.length === 0 ? (
              <Text py={4} color="gray.500" fontSize="sm">No portfolios found.</Text>
            ) : (
              portfolios.map((portfolio, index) => (
                <HStack
                  key={portfolio.id}
                  py={4}
                  borderBottom={index < portfolios.length - 1 ? "1px solid" : "none"}
                  borderColor="gray.100"
                  justify="space-between"
                  align="center"
                >
                  <HStack gap={3} flex={1} minW={0}>
                    <Box
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg={portfolio.id === activePortfolioId ? "green.400" : "gray.300"}
                      flexShrink={0}
                    />
                    {renamingId === portfolio.id ? (
                      <HStack gap={2} flex={1} minW={0}>
                        <Input
                          ref={renameInputRef}
                          size="sm"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRename()
                            if (e.key === "Escape") cancelRename()
                          }}
                          maxW="260px"
                        />
                        <Button
                          size="xs"
                          bg="green.800"
                          color="white"
                          _hover={{ bg: "green.900" }}
                          loading={renamePortfolioMutation.isPending}
                          onClick={handleRename}
                          disabled={!renameValue.trim()}
                        >
                          Save
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={cancelRename}
                        >
                          Cancel
                        </Button>
                      </HStack>
                    ) : (
                      <VStack align="start" gap={0}>
                        <HStack gap={2}>
                          <Text fontSize="sm" fontWeight="semibold">{portfolio.name}</Text>
                          {portfolio.id === activePortfolioId && (
                            <Box
                              px={2}
                              py={0.5}
                              bg="green.50"
                              color="green.700"
                              borderRadius="full"
                              fontSize="xs"
                              fontWeight="medium"
                            >
                              Active
                            </Box>
                          )}
                        </HStack>
                        <Text fontSize="xs" color="gray.400">
                          {portfolio.homeCount ?? 0} {(portfolio.homeCount ?? 0) === 1 ? "home" : "homes"}
                        </Text>
                      </VStack>
                    )}
                  </HStack>
                  {renamingId !== portfolio.id && (
                    <HStack gap={1}>
                      <Button
                        size="xs"
                        variant="ghost"
                        color="gray.600"
                        _hover={{ bg: "gray.100" }}
                        onClick={() => startRename(portfolio.id, portfolio.name)}
                      >
                        Rename
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        color="red.500"
                        _hover={{ bg: "red.50" }}
                        disabled={portfolios.length <= 1}
                        onClick={() => setConfirmDeleteId(portfolio.id)}
                      >
                        Delete
                      </Button>
                    </HStack>
                  )}
                </HStack>
              ))
            )}
          </Box>
        </Box>

        {/* Team members */}
        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200">
          <Box px={6} pt={5} pb={4} borderBottom="1px solid" borderColor="gray.100">
            <Text fontWeight="semibold" fontSize="md">Team</Text>
          </Box>
          <Box px={6} py={2}>
            {isLoadingMembers ? (
              <Box py={6}><Spinner size="sm" /></Box>
            ) : members && members.length > 0 ? (
              members.map((member, index) => {
                const isOwner = member.id === organisationOwnerId
                return (
                  <HStack
                    key={member.id}
                    py={4}
                    borderBottom={index < members.length - 1 ? "1px solid" : "none"}
                    borderColor="gray.100"
                    justify="space-between"
                  >
                    <HStack gap={3}>
                      <MemberAvatar firstName={member.firstName} lastName={member.lastName} />
                      <VStack align="start" gap={0}>
                        <Text fontSize="sm" fontWeight="semibold">
                          {member.firstName} {member.lastName}
                        </Text>
                        <Text fontSize="xs" color="gray.400">{member.email}</Text>
                      </VStack>
                    </HStack>
                    <Box
                      px={2}
                      py={0.5}
                      bg={isOwner ? "green.50" : "gray.50"}
                      color={isOwner ? "green.700" : "gray.500"}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="medium"
                    >
                      {isOwner ? "Owner" : "Member"}
                    </Box>
                  </HStack>
                )
              })
            ) : (
              <Text py={4} color="gray.500" fontSize="sm">No team members found.</Text>
            )}
          </Box>
        </Box>

      </VStack>

      {/* Delete confirmation dialog */}
      <Dialog.Root
        open={confirmDeleteId != null}
        onOpenChange={(e) => { if (!e.open) setConfirmDeleteId(null) }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content borderRadius="xl">
              <Dialog.Header pb={3}>
                <Dialog.Title fontSize="md">Delete "{portfolioToDelete?.name}"?</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body pt={0}>
                <Text fontSize="sm" color="gray.600">
                  This will permanently delete the portfolio and all its data: homes, criteria, and sourcing history. This cannot be undone.
                </Text>
              </Dialog.Body>
              <Dialog.Footer pt={4}>
                <HStack gap={2} justify="flex-end">
                  <Button size="sm" variant="outline" onClick={() => setConfirmDeleteId(null)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    bg="red.600"
                    color="white"
                    _hover={{ bg: "red.700" }}
                    loading={deletePortfolioMutation.isPending}
                    onClick={handleDelete}
                  >
                    Delete portfolio
                  </Button>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  )
}

export default Settings
